import { Link } from 'react-router-dom'
import BrandMark from './BrandMark.jsx'

/**
 * PRE-LAUNCH (2026-09-21). The Product and Company columns pointed at
 * /benefits, /pricing, /about and a firm-app signup. Those URLs are retired
 * until launch and 301 to the coming soon page, and a signup CTA is a claim
 * the product cannot be held to yet, so this footer carries only what is
 * reachable and true today. Restore the columns together with the marketing
 * routes in src/App.jsx.
 */
const COLUMNS = [
  {
    title: 'Legal',
    links: [
      { label: 'Terms', to: '/terms' },
      { label: 'Privacy', to: '/privacy' },
      { label: 'Security', to: '/security' },
    ],
  },
  {
    title: 'Contact',
    links: [
      { label: 'hello@claimguru.com', href: 'mailto:hello@claimguru.com' },
    ],
  },
]

/**
 * MarketingFooter: brand block (left) + link columns + bottom bar with
 * copyright.
 */
export default function MarketingFooter() {
  return (
    <footer
      className="border-t mt-24"
      style={{
        background: 'var(--v3-surface)',
        borderColor: 'var(--v3-border-subtle)',
        color: 'var(--v3-text-body)',
      }}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand block */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <BrandMark className="h-7 w-7" />
              <span
                className="font-semibold tracking-tight text-[15px]"
                style={{ color: 'var(--v3-text-primary)' }}
              >
                ClaimGuru
              </span>
            </Link>
            <p
              className="mt-4 max-w-md text-sm leading-relaxed"
              style={{ color: 'var(--v3-text-muted)' }}
            >
              ClaimGuru is the claims operating system for public adjusters. It
              is software that licensed public adjusting firms run their claim
              files on.
            </p>
            <p
              className="mt-4 max-w-md text-[13px] leading-relaxed"
              style={{ color: 'var(--v3-text-disabled)' }}
            >
              ClaimGuru is not a public adjuster. It does not adjust claims,
              represent policyholders, or give claim advice.
            </p>
            <p
              className="mt-5 text-[11px] tracking-[0.18em] uppercase"
              style={{ color: 'var(--v3-text-disabled)', fontFamily: 'var(--v3-font-mono)' }}
            >
              Built for property and casualty claim work
            </p>
            <p
              className="mt-3 text-[11px] tracking-[0.14em] uppercase"
              style={{ color: 'var(--v3-text-disabled)', fontFamily: 'var(--v3-font-mono)' }}
            >
              C.L.A.I.M.S.™ · Claim Lifecycle Adjustment Intelligence Management System
            </p>
            <p
              className="mt-1.5 text-[11px] tracking-[0.14em] uppercase"
              style={{ color: 'var(--v3-text-disabled)', fontFamily: 'var(--v3-font-mono)' }}
            >
              Operated by N.E.O.™ · Neural Execution Orchestrator
            </p>
          </div>

          {/* Link columns */}
          <div className="md:col-span-7 grid grid-cols-2 gap-8">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <h3
                  className="text-[11px] tracking-[0.18em] uppercase mb-4"
                  style={{ color: 'var(--v3-text-disabled)', fontFamily: 'var(--v3-font-mono)' }}
                >
                  {col.title}
                </h3>
                <ul className="flex flex-col gap-2.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      {link.to ? (
                        <Link
                          to={link.to}
                          className="text-sm transition-colors hover:opacity-100"
                          style={{ color: 'var(--v3-text-body)' }}
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-sm transition-colors hover:opacity-100"
                          style={{ color: 'var(--v3-text-body)' }}
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-14 pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-t"
          style={{ borderColor: 'var(--v3-border-subtle)' }}
        >
          <p
            className="text-xs"
            style={{ color: 'var(--v3-text-disabled)', fontFamily: 'var(--v3-font-mono)' }}
          >
            © {new Date().getFullYear()} ClaimGuru, Inc. All rights reserved.
          </p>
          <p
            className="text-[11px] tracking-[0.14em] uppercase"
            style={{ color: 'var(--v3-text-disabled)', fontFamily: 'var(--v3-font-mono)' }}
          >
            Coming soon
          </p>
        </div>
      </div>
    </footer>
  )
}
